import React from 'react';
import { useSelector } from 'react-redux';
import Banner from '@cpg-its/ads.containers.banner';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Hidden } from '@material-ui/core';
import { Redirect, useLocation } from 'react-router-dom';
import { InstitutionState } from '../institution-model';
import { usePlanSummary } from './usePlanSummary';
import { useInstitutionSetting } from '../../../components/use-institution-settings';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { ArcRoutes } from '../../../constants/routes';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { useGpsType } from '../../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import PensionPlanSummary from './pension-summary';
import AvailablePensionPlans from './available-pension-plans';
import { useQuery } from '../../../../../global/hooks/useQuery';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { PensionPlanTypes } from '../plan-history/plan-history-models';
import GhlSummary from './ghl-summary';
import { getCurrentTimeNY } from '../../../../../global/helpers/date-helpers';
import { GetSignedDocument, UnzipAndDownloadPdf } from '../../docusign/docusign-helper';
import { TcsmSiteSettingsProps } from '../../../../../global/models/epi-model';
import { DocuSignConfig } from '../../docusign/models/docusign-models';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { accordionOverrideStyles } from '../../../../../global/constants/theme/styles/components/generic-accordion';


function PlanSummary() {

    const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);
    const settings = useInstitutionSetting();
    const summaryPlanLoaded = usePlanSummary(institutionState?.institutionClientNumber);
    const planSummary = institutionState?.planSummary;
    const ghlPlans = institutionState?.ghlPlan;
    const oePlans = institutionState?.oePlanOffering;
    const institutionSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionSearch}`);
    const siteSettings = useSelector<TcsmStates, TcsmSiteSettingsProps>(state => state?.epiProps?.siteSettingsState?.siteSettings);
    const { state } = useLocation<any>();
    const gpsItem = useGpsType(GpsType.PLAN_SUMMARY);
    const successForm = useQuery().get('formType');
    const isAmend = useQuery().get('isamend');
    const buttonClasses = buttonStyles();
    const accordionClasses = accordionOverrideStyles();


    const { docuSignAppBaseUri, docuSignAccountId, docuSignAppFrameAncestorUri } = siteSettings;
    const siteBaseUri = `${window.location.protocol}//${window.location.host}`;

    const docuSignConfig: DocuSignConfig = {
        docuSignAppBaseUri,
        docuSignAppFrameAncestorUri,
        accountId: docuSignAccountId,
        siteBaseUri,
    };

    const nextYear = getCurrentTimeNY().getFullYear() + 1;

    function dowloandSignedDocument(envelopeId?: string) {
        GetSignedDocument(envelopeId || state?.request?.values?.envelopeId, docuSignConfig).then((blob) => UnzipAndDownloadPdf(blob));
    }

    function loadDownloadLink(message: string, linkText: string) {

        const anchorIndex = message.indexOf('{anchor}');
        const strippedMessage = message.replace('{anchor}', '');

        return (
            <>
                <WysiwygWithReactLink content={strippedMessage?.substring(0, anchorIndex === -1 ? strippedMessage?.length - 1 : anchorIndex)} />
                <button className={buttonClasses.linkButton} type='button' onClick={() => dowloandSignedDocument()}>
                    <PictureAsPdfIcon />{linkText}
                </button>
                <WysiwygWithReactLink content={anchorIndex !== -1 ? strippedMessage?.substring(anchorIndex) : ''} />
            </>
        );
    }

    function getSuccessMessage() {
        switch (successForm) {
            case PensionPlanTypes.LayDb:
                return loadDownloadLink(settings?.adoptLayDbSuccessMessage?.replace('[NAME]', planSummary?.lay_db?.active[0]?.name), settings?.adoptLayDbDownloadFormLinkText);
            case PensionPlanTypes.LayDc:
                return isAmend ?
                    loadDownloadLink(settings?.amendLayDCSuccessMessage?.replace('[NAME]', planSummary?.dc?.active[0]?.name), settings?.amendLayDCDownloadFormLinkText) :
                    loadDownloadLink(settings?.adoptLayDCSuccessMessage?.replace('[NAME]', planSummary?.dc?.active[0]?.name), settings?.adoptLayDCDownloadFormLinkText);
            case PensionPlanTypes.Rsvp:
                return isAmend ?
                    loadDownloadLink(settings?.amendRSVPSuccessMessage?.replace('[NAME]', planSummary?.rsvp?.active[0]?.name), settings?.amendRSVPDownloadFormLinkText) :
                    loadDownloadLink(settings?.adoptRSVPSuccessMessage?.replace('[NAME]', planSummary?.rsvp?.active[0]?.name), settings?.adoptRSVPDownloadFormLinkText);
            case PensionPlanTypes.OePlans:
                return <WysiwygWithReactLink content={settings?.declineMedicalDentalSuccessMessage?.replace('{year}', nextYear?.toString())} />;
            default:
                return null;
        }
    }


    if (!institutionState?.institutionClientNumber) {
        return (
            <Redirect to=
                {
                    {
                        pathname: institutionSelect,
                        state,
                    }
                }
            />);
    }


    if (!summaryPlanLoaded) {
        return <LoadingCircle />;
    }

    if (!planSummary) {
        return (<p>{settings?.noPlanSummaryMessage}</p>);
    }

    return (
        <PageContainer>
            <MainContent>
                {
                    successForm &&
                    <ContentSpacer>
                        <Banner banner={{
                            type: 'success',
                            label: getSuccessMessage(),
                        }} />
                    </ContentSpacer>
                }
                {
                    settings?.allowPlanSelectionPeriod &&
                    <ContentSpacer>
                        <Banner banner={{
                            type: 'info',
                            label: <WysiwygWithReactLink content={settings?.planSelectionPeriodMessage} />,
                        }} />
                    </ContentSpacer>
                }
                <PageHeader
                    headerText={settings?.planSummaryTitle}
                    description={settings?.planSummaryDescription}
                />
                <Hidden mdUp>
                    <SideBarContent>
                        <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                    </SideBarContent>
                </Hidden>
                <DividerSection sectionText={settings?.myPlansSectionTitle} >
                    <div className={`${accordionClasses.accordionContainer} border-bottom`}>
                        <Accordion>
                            <AccordionSummary className='MuiAccordionSummary-root-parent' expandIcon={<ExpandMoreIcon />}>
                                <h3>{settings?.currentPlansTitle}</h3>
                            </AccordionSummary>
                            <AccordionDetails >
                                <div className='MuiAccordionDetails-root-inner'>
                                    <PensionPlanSummary settings={settings} planSummary={planSummary} downloadPdfAction={dowloandSignedDocument} />
                                    <GhlSummary settings={settings} ghlPlans={ghlPlans} oePlans={oePlans} />
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    </div>
                </DividerSection>
                <AvailablePensionPlans settings={settings} planSummary={planSummary} />
            </MainContent>
            <Hidden smDown>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </Hidden>
        </PageContainer>
    );
}

export default PlanSummary;